import React, { useEffect, useRef, useState } from 'react';
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  getMultiFactorResolver,
  signInWithPopup,
} from 'firebase/auth';
import {
  Grid2,
  TextField,
  Typography,
  Container,
  Box,
  Alert,
  InputLabel,
  InputAdornment,
  IconButton,
  Checkbox,
  CircularProgress,
  useTheme,
  Button,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MFASecondFactor from './MFASecondFactor';
import { posSizingTableTitleSx } from './pos_sizing_table/tableStyles';
import {
  loginRoot,
  loginContainer,
  loginLeftSection,
  loginRightSection,
  loginTextContainer,
  loginForm,
  forgtPasswordBoxContainer,
  forgetPassword,
} from '../styles/tailwindClasses';
import loginPageImage from '../assets/LoginPageNY.jpg';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { IceButton } from '../styles/authStyles';
import { auth } from '../firebase';
import { authPanelSx } from '../styles/secondaryMsiStyles';
import {
  APP_STYLE_PALETTE as MSI_PALETTE,
  usesAppStyleGrammar,
} from '../styles/msiStyleTokens';
import {
  createGoogleAuthProvider,
  createMicrosoftAuthProvider,
  describeAuthError,
} from '../utils/federatedAuth';

const LableLoginPage = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '16px',
  color: theme.palette.mode === 'dark' ? theme.palette.common.white : 'rgb(55, 65, 81)',
  fontFamily: '"Roboto Mono", monospace',
  display: 'block',
  marginBottom: '8px',
}));

const modernCapsFontFamily = {
  fontFamily: (theme) => theme.typography.fontFamily,
};

const loginFieldLabelSx = {
  ...posSizingTableTitleSx,
  ...modernCapsFontFamily,
  color: 'text.secondary',
  mb: 1,
};

const loginHeroKickerSx = {
  ...posSizingTableTitleSx,
  ...modernCapsFontFamily,
  color: 'rgba(241, 245, 249, 0.9)',
  fontSize: '0.9rem',
  letterSpacing: '0.16em',
  mb: 0.75,
};

const loginHeroTitleSx = {
  ...posSizingTableTitleSx,
  ...modernCapsFontFamily,
  color: '#f8fafc',
  fontWeight: 800,
  fontSize: { xs: '2rem', md: '2.4rem' },
  letterSpacing: '0.14em',
  lineHeight: 1.05,
};

const loginInputSx = {
  '& .MuiInputBase-input': {
    fontFamily: (theme) => theme.typography.fontFamily,
    fontSize: 14,
    letterSpacing: (theme) => (usesAppStyleGrammar(theme) ? 0 : '0.02em'),
  },
};

const providerButtonSx = {
  minHeight: 50,
  justifyContent: 'center',
  borderRadius: 2,
  borderColor: 'divider',
  bgcolor: 'background.paper',
  color: 'text.primary',
  fontSize: 14,
  fontWeight: 650,
  textTransform: 'none',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
  '&:hover': {
    borderColor: 'primary.main',
    bgcolor: 'action.hover',
  },
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: 2,
  },
};

function GoogleMark() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.39-.18-2.05H12v3.87h5.38a4.61 4.61 0 0 1-1.99 3.03v2.51h3.22c1.88-1.73 2.99-4.28 2.99-7.36Z" />
      <path fill="#34A853" d="M12 22c2.7 0 4.96-.9 6.61-2.41l-3.22-2.51c-.89.6-2.03.96-3.39.96-2.61 0-4.82-1.76-5.61-4.12H3.07v2.59A10 10 0 0 0 12 22Z" />
      <path fill="#FBBC05" d="M6.39 13.92a6.02 6.02 0 0 1 0-3.84V7.49H3.07a10 10 0 0 0 0 9.02l3.32-2.59Z" />
      <path fill="#EA4335" d="M12 5.96c1.43 0 2.72.49 3.74 1.48l2.8-2.8A9.55 9.55 0 0 0 12 2a10 10 0 0 0-8.93 5.49l3.32 2.59C7.18 7.72 9.39 5.96 12 5.96Z" />
    </svg>
  );
}

function MicrosoftMark() {
  return (
    <svg width="20" height="20" viewBox="0 0 22 22" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="12" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="12" width="9" height="9" fill="#00A4EF" />
      <rect x="12" y="12" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}


function Login({ initialError = '', onAuthAttempt }) {
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState(initialError);
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');
  const [mfaResolver, setMfaResolver] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [authOpPending, setAuthOpPending] = useState(null);
  const emailInputRef = useRef(null);

  useEffect(() => {
    setLoginError(initialError || '');
  }, [initialError]);

  useEffect(() => {
    if (showEmailForm) emailInputRef.current?.focus();
  }, [showEmailForm]);

  const beginAuthAttempt = (operation) => {
    onAuthAttempt?.();
    setLoginError('');
    setResetMessage('');
    setResetError('');
    setAuthOpPending(operation);
  };

  const handleSignInError = (error, method) => {
    if (error?.code === 'auth/multi-factor-auth-required') {
      const resolver = getMultiFactorResolver(auth, error);
      setMfaResolver(resolver);
      return;
    }

    if (method === 'password' && ![
      'auth/user-disabled',
      'auth/admin-restricted-operation',
      'auth/operation-not-allowed',
    ].includes(error?.code)) {
      setLoginError('Invalid Email or Password');
      return;
    }

    setLoginError(describeAuthError(error));
  };


  // Handle email/password login with MFA support
  const handleLogin = async (event) => {
    event.preventDefault();
    if (authOpPending) return;
    beginAuthAttempt('password');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      handleSignInError(error, 'password');
    } finally {
      setAuthOpPending(null);
    }
  };

  const handleFederatedLogin = async (method) => {
    if (authOpPending) return;
    beginAuthAttempt(method);
    try {
      const provider = method === 'google'
        ? createGoogleAuthProvider()
        : createMicrosoftAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error) {
      handleSignInError(error, method);
    } finally {
      setAuthOpPending(null);
    }
  };

  const handleResetPassword = async () => {
    if (authOpPending) return;
    setResetMessage('');
    setResetError('');
    if (!email) {
      setResetError("Please enter your email address above.");
      return;
    }
    try {
      setAuthOpPending('reset');
      await sendPasswordResetEmail(auth, email);
      setResetMessage("Password reset email sent! Check your inbox.");
    } catch (error) {
      setResetError(describeAuthError(error));
    } finally {
      setAuthOpPending(null);
    }
  };

  return (
    <div className={loginRoot} style={{
      backgroundColor: theme.palette.background.default
    }}>
      <Grid2 container className={loginContainer} >
        <Grid2 size={{ xs: 12, md: 5, lg: 4 }} className={loginLeftSection} sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: '100vh',
          py: { xs: 6, md: 8 },
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          ...authPanelSx,
        }}>
          <Box sx={{ width: '100%', maxWidth: 390, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 5 }}>
              <Box aria-hidden="true" sx={{
                width: 38,
                height: 38,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontSize: 20,
                fontWeight: 800,
              }}>A</Box>
              <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.12em' }}>
                ALPHAMIZER
              </Typography>
            </Box>
            <Typography component="h1" sx={{ fontSize: { xs: 29, sm: 32 }, fontWeight: 750, lineHeight: 1.15, mb: 1 }}>
              Welcome back
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.55, mb: 3.5 }}>
              Sign in to your portfolio workspace.
            </Typography>
            {loginError && <Alert severity="error" sx={{ mb: 2 }}>{loginError}</Alert>}
            {resetMessage && <Alert severity="success" sx={{ mb: 2 }}>{resetMessage}</Alert>}
            {resetError && <Alert severity="error" sx={{ mb: 2 }}>{resetError}</Alert>}
            {mfaResolver ? (
              <MFASecondFactor resolver={mfaResolver} onSuccess={() => setMfaResolver(null)} />
            ) : (
              <>
                <Box sx={{ display: 'grid', gap: 1.25 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    fullWidth
                    sx={providerButtonSx}
                    startIcon={authOpPending === 'google' ? <CircularProgress size={18} color="inherit" /> : <GoogleMark />}
                    data-testid="login-google-button"
                    disabled={Boolean(authOpPending)}
                    onClick={() => handleFederatedLogin('google')}
                  >
                    {authOpPending === 'google' ? 'Opening Google…' : 'Continue with Google'}
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    fullWidth
                    sx={providerButtonSx}
                    startIcon={authOpPending === 'microsoft' ? <CircularProgress size={18} color="inherit" /> : <MicrosoftMark />}
                    data-testid="login-microsoft-button"
                    disabled={Boolean(authOpPending)}
                    onClick={() => handleFederatedLogin('microsoft')}
                  >
                    {authOpPending === 'microsoft' ? 'Opening Microsoft…' : 'Continue with Microsoft'}
                  </Button>
                </Box>
                <Divider sx={{ my: 2.5, color: 'text.secondary', fontSize: 12 }}>OR</Divider>
                {!showEmailForm ? (
                  <Button
                    type="button"
                    variant="outlined"
                    fullWidth
                    sx={providerButtonSx}
                    startIcon={<EmailOutlinedIcon sx={{ fontSize: 20 }} />}
                    disabled={Boolean(authOpPending)}
                    onClick={() => setShowEmailForm(true)}
                  >
                    Continue with email
                  </Button>
                ) : (
                  <Box component="form" aria-label="Sign in" className={loginForm} onSubmit={handleLogin}>
                    <Box>
                      <LableLoginPage component="label" htmlFor="username" sx={loginFieldLabelSx}>
                        Email
                      </LableLoginPage>
                      <TextField
                        id="username"
                        name="email"
                        type="email"
                        autoComplete="username"
                        variant="outlined"
                        sx={loginInputSx}
                        inputRef={emailInputRef}
                        fullWidth
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={Boolean(authOpPending)}
                      />
                    </Box>

                    <Box>
                      <LableLoginPage component="label" htmlFor="password" sx={loginFieldLabelSx}>
                        Password
                      </LableLoginPage>
                      <TextField
                        id="password"
                        name="password"
                        variant="outlined"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        fullWidth
                        value={password}
                        InputProps={{
                          style: { fontSize: 14 },
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                type="button"
                                disabled={Boolean(authOpPending)}
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                size="small"
                              >
                                <span className="material-icons" style={{ fontSize: 20 }}>
                                  {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                </span>
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={loginInputSx}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={Boolean(authOpPending)}
                        required
                      />
                    </Box>
                    <Box className={forgtPasswordBoxContainer}
                      sx={{
                        fontFamily: (theme) => (usesAppStyleGrammar(theme) ? theme.typography.fontFamily : "'Roboto Mono', monospace"),
                        fontSize: 12,
                        marginTop: 1,
                        color: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.blue600 : '#6366f1'),
                        fontWeight: 600,
                      }}>
                      <Button
                        type="button"
                        variant="text"
                        size="small"
                        disabled={Boolean(authOpPending)}
                        onClick={handleResetPassword}
                        className={forgetPassword}
                      >
                        Forgot password?
                      </Button>
                    </Box>
                    <Box mt={2} display="flex" justifyContent="space-between">
                      <IceButton
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={Boolean(authOpPending)}
                        startIcon={authOpPending === 'password' ? <CircularProgress size={20} color="inherit" /> : null}
                      >
                        {authOpPending === 'password' ? 'Signing In...' : 'Sign In'}
                      </IceButton>
                    </Box>
                  </Box>
                )}
                <Typography color="text.secondary" sx={{ fontSize: 12, lineHeight: 1.5, mt: 4 }}>
                  New to Alphamizer? Ask your administrator for an invitation.
                </Typography>
              </>
            )}
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 7, lg: 8 }} sx={{ display: { xs: 'none', md: 'flex' }, backgroundColor: theme.palette.background.paper }}>
          <div className={loginRightSection} style={{ backgroundImage: `url(${loginPageImage})` }}>
            <div className={loginTextContainer} aria-hidden="true">
              <Typography component="p" sx={loginHeroKickerSx}>
                Welcome To
              </Typography>
              <Typography component="div" sx={loginHeroTitleSx}>
                Alphamizer
              </Typography>
            </div>
            <div className="login-overlay" aria-hidden="true" />
          </div>
        </Grid2>
      </Grid2>
    </div>
  );
}

export default Login;
